import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { HardDrive, Download, Trash2, RefreshCw, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * BLM Cache & Disk tile — super-admin-only operational panel.
 *
 * Shows:
 *   - Per-band BLM PDF presence under /app/dist/blm_production_cache/
 *     (3-4, 5-6, 7-8, 9-12 — K/1/2 always ship inside the pod).
 *   - /app disk headroom; whether the pod is mint-ready (≥ 1 GiB free).
 *   - "Pull now" per band → materializes approved PDFs from R2.
 *   - "Clean old builds" → reclaims disk by removing stale
 *     /app/usb_images/*.tar.gz artifacts.
 *
 * Everything hits `/api/admin/blm-cache/*` (super-admin gated). No
 * mint-pipeline state is touched here.
 */
const Row = ({ label, children, testid }) => (
  <div data-testid={testid} style={{
    display: 'grid', gridTemplateColumns: '110px 1fr auto',
    gap: 10, alignItems: 'center',
    padding: '8px 0', borderBottom: '1px solid #F3F4F6',
    fontSize: 13,
  }}>
    <div style={{ fontWeight: 600, color: '#374151' }}>{label}</div>
    {children}
  </div>
);

const bandColor = (present, expected) => {
  if (expected === 0) return '#9CA3AF';
  if (present === 0) return '#DC2626';
  if (present < expected) return '#D97706';
  return '#059669';
};

const BlmCacheTile = () => {
  const [status, setStatus] = useState(null);
  const [disk, setDisk] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // band or 'disk' or 'clean'
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);

  const authHeaders = useCallback(() => {
    const token = localStorage.getItem('session_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [s, d] = await Promise.all([
        axios.get(`${API}/api/admin/blm-cache/status`, { headers: authHeaders() }),
        axios.get(`${API}/api/admin/blm-cache/disk`,   { headers: authHeaders() }),
      ]);
      setStatus(s.data);
      setDisk(d.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { refresh(); }, [refresh]);

  const pullBand = async (band) => {
    setBusy(band); setError(null); setNote(null);
    try {
      const r = await axios.post(
        `${API}/api/admin/blm-cache/pull`,
        { bands: [band] },
        { headers: { ...authHeaders(), 'Content-Type': 'application/json' }, timeout: 300000 },
      );
      setNote(`Band ${band}: pulled ${r.data.pulled_count}, skipped ${r.data.skipped_count}, failures ${r.data.failure_count}.`);
      await refresh();
    } catch (e) {
      setError(`Pull ${band} failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(null);
    }
  };

  const cleanBuilds = async () => {
    if (!window.confirm('Delete all /app/usb_images/*.tar.gz build artifacts? Shipped drives are preserved on R2; this only clears local staging. Proceed?')) return;
    setBusy('clean'); setError(null); setNote(null);
    try {
      const r = await axios.post(
        `${API}/api/admin/blm-cache/clean-builds`,
        { dry_run: false },
        { headers: { ...authHeaders(), 'Content-Type': 'application/json' } },
      );
      setNote(`Cleaned ${r.data.removed_count} entries, freed ${r.data.freed_mb} MB.`);
      await refresh();
    } catch (e) {
      setError(`Clean failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div data-testid="blm-cache-tile-loading" style={tileStyle}>
        <Loader2 size={14} className="animate-spin" /> Loading BLM cache status…
      </div>
    );
  }

  const mintReady = disk?.mint_ready;

  return (
    <div data-testid="blm-cache-tile" style={tileStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <HardDrive size={16} color="#002FA7" />
          <h3 style={{ margin: 0, fontSize: 14, color: '#0F1B2D' }}>BLM Cache &amp; Disk</h3>
          <span style={{
            padding: '2px 8px', fontSize: 11, fontWeight: 700,
            background: mintReady ? '#D1FAE5' : '#FEE2E2',
            color:      mintReady ? '#047857' : '#B91C1C',
            borderRadius: 999,
          }}>
            {mintReady ? 'MINT READY' : 'DISK FULL'}
          </span>
        </div>
        <button data-testid="blm-cache-refresh" onClick={refresh}
          style={ghostBtn} disabled={!!busy}>
          <RefreshCw size={11} /> Refresh
        </button>
      </div>

      {error && (
        <div data-testid="blm-cache-error" style={errorStyle}>
          <AlertCircle size={12} /> {String(error)}
        </div>
      )}
      {note && (
        <div data-testid="blm-cache-note" style={noteStyle}>
          <CheckCircle2 size={12} /> {note}
        </div>
      )}

      {/* Disk row */}
      <Row label="/app disk" testid="blm-cache-disk-row">
        <div style={{ color: '#4B5563' }}>
          <span style={{ fontWeight: 700, color: mintReady ? '#047857' : '#B91C1C' }}>
            {Math.round((disk?.free_bytes || 0) / 1024 / 1024)} MB free
          </span>
          {' · '}{disk?.percent_used}% used of {Math.round((disk?.total_bytes || 0) / 1024 / 1024 / 1024)} GB
          {' · '}{disk?.artifact_count} artifact(s) in /app/usb_images
        </div>
        <button data-testid="blm-cache-clean-btn" onClick={cleanBuilds}
          style={{ ...dangerBtn, opacity: busy ? 0.6 : 1 }} disabled={!!busy}>
          {busy === 'clean' ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />}
          &nbsp;Clean old builds
        </button>
      </Row>

      {/* K/1/2 */}
      <Row label="K / 1 / 2" testid="blm-cache-k2-row">
        <div style={{ color: '#4B5563' }}>
          Shipped on-pod · K={status?.k2_pdf_counts?.K} · 1={status?.k2_pdf_counts?.['1']} · 2={status?.k2_pdf_counts?.['2']}
        </div>
        <span style={{ fontSize: 11, color: '#9CA3AF' }}>no pull needed</span>
      </Row>

      {/* R2-sourced bands */}
      {['3-4', '5-6', '7-8', '9-12'].map((band) => {
        const info = status?.bands?.[band] || { present_count: 0, expected_count: 0, verified_count: 0 };
        const color = bandColor(info.present_count, info.expected_count);
        return (
          <Row key={band} label={`Band ${band}`} testid={`blm-cache-band-${band}`}>
            <div style={{ color: '#4B5563' }}>
              <span style={{ fontWeight: 700, color }}>
                {info.present_count}/{info.expected_count} PDFs
              </span>
              {' · '}verified: {info.verified_count}
              {info.band_dir_exists ? '' : ' · dir missing'}
            </div>
            <button
              data-testid={`blm-cache-pull-${band}`}
              onClick={() => pullBand(band)}
              style={{ ...primaryBtn, opacity: busy ? 0.6 : 1 }}
              disabled={!!busy}
            >
              {busy === band ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />}
              &nbsp;Pull now
            </button>
          </Row>
        );
      })}

      <div style={{ marginTop: 10, fontSize: 11, color: '#9CA3AF' }}>
        Cache root: {status?.cache_root_resolved} {status?.cache_root_exists ? '' : '(dangling)'}
      </div>
    </div>
  );
};

const tileStyle = {
  background: '#fff',
  border: '1px solid #E5E7EB',
  borderLeft: '3px solid #002FA7',
  padding: 14,
  marginBottom: 16,
  fontSize: 13,
};
const ghostBtn = {
  padding: '4px 10px', fontSize: 11, fontWeight: 600,
  background: '#fff', color: '#374151',
  border: '1px solid #D1D5DB', cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: 4,
};
const primaryBtn = {
  padding: '4px 10px', fontSize: 11, fontWeight: 600,
  background: '#002FA7', color: '#fff',
  border: '1px solid #002FA7', cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: 4,
};
const dangerBtn = {
  padding: '4px 10px', fontSize: 11, fontWeight: 600,
  background: '#fff', color: '#B91C1C',
  border: '1px solid #FCA5A5', cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: 4,
};
const errorStyle = {
  background: '#FEE2E2', border: '1px solid #FCA5A5', color: '#B91C1C',
  padding: 8, marginBottom: 10, fontSize: 12,
  display: 'inline-flex', alignItems: 'center', gap: 6, width: '100%',
};
const noteStyle = {
  background: '#D1FAE5', border: '1px solid #6EE7B7', color: '#047857',
  padding: 8, marginBottom: 10, fontSize: 12,
  display: 'inline-flex', alignItems: 'center', gap: 6, width: '100%',
};

export default BlmCacheTile;
